import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import {
  fetchBuilding,
  createBuilding,
  updateBuilding,
  deleteBuilding,
  fetchBuildingFinancialSummary,
} from "../../api/buildingsApi";
import { useBuilding } from "../../context/BuildingContext";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import Tabs from "../../components/common/Tabs";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import PageHeader from "../../components/common/PageHeader";
import {
  Address,
  Building,
  FinancialSummary,
  BuildingRole,
  ServiceChargeFrequency,
  formatAddress,
} from "@akfdev-com/shared/src/models";
import { parseLegacyAddress } from "@akfdev-com/shared/src/geocoding/parseLegacyAddress";
import type { PartialAddress } from "../../components/ui";

// Building sections
import BuildingOverview from "../../components/buildings/BuildingOverview";
import BuildingUnits from "../../components/buildings/BuildingUnits";
import BuildingBills from "../../components/buildings/BuildingBills";
import BuildingBudgets from "../../components/buildings/BuildingBudgets";
import BuildingDocuments from "../../components/buildings/BuildingDocuments";
import BuildingSettings from "../../components/buildings/BuildingSettings";

const BuildingDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  const { user } = useUser();
  const { setCurrentBuilding } = useBuilding();

  const [building, setBuilding] = useState<Building | null>(null);
  const [financialSummary, setFinancialSummary] =
    useState<FinancialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || "overview",
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Building>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const loadBuilding = async () => {
      if (!id) {
        console.debug(
          "[BuildingDetails] No building ID found in params. Skipping load.",
        );
        return;
      }
      // Skip API fetch when creating a new building
      if (id === "new") {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const token = (await getToken()) || "";
        // console.debug('[BuildingDetails] Fetching building with ID:', id, 'using token:', token);
        const buildingData = await fetchBuilding(id, token);
        console.debug("[BuildingDetails] API response:", buildingData);

        // Ensure required properties exist (fallbacks if missing)
        if (!buildingData || !buildingData.id) {
          console.warn(
            "[BuildingDetails] No building data or missing ID:",
            buildingData,
          );
          setBuilding(null);
          setLoading(false);
          return;
        }
        // Spread the API response first so additive optional fields
        // (structuredAddress, location, notificationPreferences, status,
        // contactEmail, contactPhone, description, managementCompany,
        // yearBuilt, locationIdentifiers) are preserved. Then override
        // required-with-default fields to guarantee the Building contract.
        const completeBuildingData: Building = {
          ...buildingData,
          ownerId: buildingData.ownerId ?? "",
          unitsCount: buildingData.unitsCount ?? 0,
          numberOfUnits: buildingData.numberOfUnits ?? 0,
          // The API contract (shared BuildingRole enum) is lowercase-value.
          // Prior code did a PascalCase-key lookup that never matched real
          // values and silently coerced everything to Admin — removed.
          role: buildingData.role ?? BuildingRole.Admin,
          serviceChargeFrequency:
            buildingData.serviceChargeFrequency ??
            ServiceChargeFrequency.Monthly,
          updatedAt: buildingData.updatedAt ?? "",
        };

        setBuilding(completeBuildingData);
        // Pre-fill the edit form. When the record has no stored
        // `structuredAddress` but does carry a legacy string, run a
        // pure heuristic parse so the AddressForm renders pre-filled
        // fields instead of forcing the user to retype. Same UX as
        // ContactEdit.
        const editFormSeed: Partial<Building> =
          !completeBuildingData.structuredAddress &&
          completeBuildingData.address
            ? {
                ...completeBuildingData,
                structuredAddress: parseLegacyAddress(
                  completeBuildingData.address,
                ) as Address,
              }
            : completeBuildingData;
        setEditForm(editFormSeed);

        // Update the current building in context
        console.debug("[BuildingDetails] Calling setCurrentBuilding with:", {
          id: buildingData.id,
          name: buildingData.name,
          address: buildingData.address,
          role: buildingData.role ?? BuildingRole.Admin,
        });
        setCurrentBuilding({
          id: buildingData.id,
          name: buildingData.name,
          address: buildingData.address,
          role: buildingData.role ?? BuildingRole.Admin, // This should come from the API response
        });

        // Fetch the live financial summary from the API. Values are in pence.
        try {
          const summary = await fetchBuildingFinancialSummary(
            buildingData.id,
            token,
          );
          setFinancialSummary(summary);
        } catch (summaryErr) {
          console.warn(
            "[BuildingDetails] Failed to load financial summary:",
            summaryErr,
          );
          setFinancialSummary(null);
        }
      } catch (err) {
        setError("Failed to load building details. Please try again later.");
        console.error("[BuildingDetails] Error loading building details:", err);
      } finally {
        setLoading(false);
      }
    };

    console.debug(
      "[BuildingDetails] useEffect triggered. id:",
      id,
      "getToken:",
      getToken,
      "setCurrentBuilding:",
      setCurrentBuilding,
    );
    loadBuilding();
  }, [id, getToken /* setCurrentBuilding cuased infinate loop*/]);

  const handleCreateBuilding = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = (await getToken()) || "";
      const ownerId =
        (user?.publicMetadata?.ownerId as string) || user?.id || "";
      const requestedUnits = editForm.unitsCount ?? editForm.numberOfUnits ?? 0;
      const response = await createBuilding(
        {
          ...editForm,
          ownerId,
          name: editForm.name || "",
          address: editForm.address || "",
          unitsCount: requestedUnits,
          role: editForm.role || BuildingRole.Admin,
          // Send both for backend forward-compat (Rule 15 accepts either).
          numberOfUnits: requestedUnits,
          serviceChargeFrequency:
            editForm.serviceChargeFrequency || ServiceChargeFrequency.Monthly,
        } as any,
        token,
      );
      const newId = (response as any)?.id;
      const unitsCreated = (response as any)?.unitsCreated ?? 0;
      // Surface the create-side-effect via a browser alert until we wire
      // a toast primitive (buildings task 1.8). Alert is intentionally
      // ugly so it's obvious this needs a toast follow-up.
      if (unitsCreated > 0 && typeof window !== "undefined") {
        // eslint-disable-next-line no-console
        console.info(
          `[Buildings] Created building with ${unitsCreated} outline units`,
        );
      }
      navigate(
        newId
          ? `/dashboard/buildings/${newId}?tab=units`
          : "/dashboard/buildings",
      );
    } catch (err) {
      setError("Failed to create building. Please try again.");
      console.error("Error creating building:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!building || !id) return;

    try {
      setLoading(true);
      const token = (await getToken()) || "";
      await updateBuilding(id, editForm, token);

      // Refresh building data
      const updatedBuildingData = await fetchBuilding(id, token);
      // Same shape as loadBuilding — spread the response so
      // structuredAddress / location / notificationPreferences / status
      // survive the refresh, then override required-with-default fields.
      const completeUpdatedBuilding: Building = {
        ...updatedBuildingData,
        ownerId: updatedBuildingData.ownerId ?? "",
        unitsCount: updatedBuildingData.unitsCount ?? 0,
        numberOfUnits: updatedBuildingData.numberOfUnits ?? 0,
        // See loadBuilding for why the role-normalization branch was removed.
        role: updatedBuildingData.role ?? BuildingRole.Admin,
        serviceChargeFrequency:
          updatedBuildingData.serviceChargeFrequency ??
          ServiceChargeFrequency.Monthly,
        updatedAt: updatedBuildingData.updatedAt ?? "",
      };
      setBuilding(completeUpdatedBuilding);
      setIsEditing(false);

      // Update building in context if name or address changed
      if (
        editForm.name !== building.name ||
        editForm.address !== building.address
      ) {
        setCurrentBuilding({
          id: building.id,
          name: editForm.name || building.name,
          address: editForm.address || building.address,
          role: editForm.role ?? building.role,
        });
      }
    } catch (err) {
      setError("Failed to update building. Please try again.");
      console.error("Error updating building:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBuilding = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const token = (await getToken()) || "";
      await deleteBuilding(id, token);

      // Navigate back to buildings list
      navigate("/dashboard/buildings");
    } catch (err) {
      setError("Failed to delete building. Please try again.");
      console.error("Error deleting building:", err);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Notification-preferences handler (buildings Rule 20). Merges the new
   * three-key object into `editForm.notificationPreferences` and persists
   * on Save via the existing `handleSaveChanges` flow which does a
   * `PUT /buildings/{id}` with the whole editForm.
   */
  const handleNotificationPreferencesChange = (next: {
    statementIssued: boolean;
    expenseCreated: boolean;
    paymentReminder: boolean;
  }) => {
    setEditForm((prev) => ({ ...prev, notificationPreferences: next }));
  };

  /**
   * Archive handler (buildings Rule 20 · Danger Zone). Uses the same
   * `deleteBuilding` API today (soft-delete semantics belong to the
   * backend). When task 4.5 lands with a dedicated `POST /{id}/archive`
   * endpoint that flips `status = 'archived'` without hard-deleting,
   * swap this to use the new endpoint. On success, navigate back to
   * the buildings list.
   */
  const handleArchiveBuilding = async (): Promise<void> => {
    if (!id) return;
    try {
      setLoading(true);
      const token = (await getToken()) || "";
      await deleteBuilding(id, token);
      navigate("/dashboard/buildings");
    } catch (err) {
      setError("Failed to archive building. Please try again.");
      console.error("Error archiving building:", err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Structured-address handler for the <AddressForm> primitive
   * (address-geolocation Rule 21). Updates `editForm.structuredAddress` with
   * the merged partial AND re-derives the legacy `editForm.address` string
   * via `formatAddress` so back-compat readers continue to work
   * (address-geolocation Req 11.1, 11.2).
   *
   * The stored `Building.structuredAddress` is typed as `Address` (required
   * fields present), but during editing we accept a PartialAddress; save-time
   * cast preserves the type contract. Never mutates `next`.
   */
  const handleStructuredAddressChange = (next: PartialAddress) => {
    // Build a shape that formatAddress can safely consume — missing required
    // fields are dropped from the joined string (empty parts are skipped).
    const asAddressForFormat: Address = {
      organisation: next.organisation,
      addressLine1: next.addressLine1 ?? "",
      addressLine2: next.addressLine2,
      locality: next.locality,
      town: next.town ?? "",
      county: next.county,
      postcode: next.postcode ?? "",
      countryCode: next.countryCode ?? "",
    };
    setEditForm((prev) => ({
      ...prev,
      structuredAddress: next as Address,
      address: formatAddress(asAddressForFormat),
    }));
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "units", label: "Units" },
    { id: "bills", label: "Bills" },
    { id: "budgets", label: "Budgets" },
    { id: "documents", label: "Documents" },
    { id: "settings", label: "Settings" },
  ];

  if (loading && !building) {
    return <Spinner message="Loading building details..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (!building) {
    // Create mode — form open immediately, same pattern as UnitDetails for new units
    return (
      <div className="">
        <PageHeader
          title="New Building"
          description="Enter your building's details below."
          actions={
            <div className="flex space-x-2">
              <button
                onClick={() => navigate("/dashboard/buildings")}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBuilding}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none disabled:opacity-50"
              >
                Save Building
              </button>
            </div>
          }
        />
        {error && (
          <div className="mt-4">
            <ErrorAlert message={error} />
          </div>
        )}
        <div className="mt-6 bg-white shadow rounded-lg">
          <BuildingOverview
            building={null}
            financialSummary={null}
            isEditing={true}
            editForm={editForm}
            handleInputChange={handleInputChange}
            onStructuredAddressChange={handleStructuredAddressChange}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <PageHeader
        title={building.name}
        description={
          building.structuredAddress
            ? formatAddress(building.structuredAddress)
            : building.address
        }
        actions={
          <div className="flex space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={() => {
                    setEditForm(building);
                    setIsEditing(false);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
                >
                  Save Changes
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Edit
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        }
      />

      <div className="mt-8">
        <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      <div className="mt-6 bg-white shadow rounded-lg">
        {activeTab === "overview" && (
          <BuildingOverview
            building={building}
            financialSummary={financialSummary}
            isEditing={isEditing}
            editForm={editForm}
            handleInputChange={handleInputChange}
            onStructuredAddressChange={handleStructuredAddressChange}
          />
        )}

        {activeTab === "units" && <BuildingUnits buildingId={building.id} />}

        {activeTab === "bills" && <BuildingBills buildingId={building.id} />}

        {activeTab === "budgets" && (
          <BuildingBudgets buildingId={building.id} />
        )}

        {activeTab === "documents" && (
          <BuildingDocuments buildingId={building.id} />
        )}

        {activeTab === "settings" && (
          <BuildingSettings
            building={building}
            isEditing={isEditing}
            editForm={editForm}
            handleInputChange={handleInputChange}
            onStructuredAddressChange={handleStructuredAddressChange}
            onNotificationPreferencesChange={
              handleNotificationPreferencesChange
            }
            onArchive={handleArchiveBuilding}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteBuilding}
        title="Delete Building"
        message={`Are you sure you want to delete "${building.name}"? This action cannot be undone and will remove all associated data.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
};

export default BuildingDetails;
